class InstamartCollectionNavigation {
  constructor() {
    this.currentPath = window.location.pathname;
    this.abortController = null;
    this.onClick = this.onClick.bind(this);
    this.onPopState = this.onPopState.bind(this);
    document.addEventListener('click', this.onClick, true);
    window.addEventListener('popstate', this.onPopState);
    window.requestAnimationFrame(() => this.loadDefaultCollection());
  }

  withPreviewTheme(url) {
    const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);
    return url;
  }

  loadDefaultCollection() {
    const section = document.querySelector('collection-component .instamart-collection');
    const defaultUrl = section?.dataset.defaultCollectionUrl;
    if (!section || section.dataset.hasMatchingCollection === 'true' || !defaultUrl) return;

    const url = this.withPreviewTheme(new URL(defaultUrl, window.location.origin));
    if (url.pathname === window.location.pathname) return;

    history.replaceState({ instamartCollectionSwitch: true }, '', `${url.pathname}${url.search}`);
    this.load(url, false);
  }

  onClick(event) {
    const link = event.target.closest('.instamart-collection__rail-item');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin) return;
    this.withPreviewTheme(url);

    event.preventDefault();
    if (url.pathname === window.location.pathname && url.search === window.location.search) return;

    if (!history.state?.instamartCollectionSwitch) {
      history.replaceState({ instamartCollectionSwitch: true }, '', window.location.href);
    }

    this.load(url, true);
  }

  onPopState(event) {
    if (!event.state?.instamartCollectionSwitch) return;
    this.load(new URL(window.location.href), false);
  }

  async load(url, updateHistory) {
    const currentSection = document.querySelector('collection-component .instamart-collection');
    const currentComponent = currentSection?.closest('collection-component');
    const currentContent = currentSection?.querySelector('.instamart-collection__content');
    const sectionId = currentSection?.dataset.sectionId;
    if (!currentComponent || !currentContent || !sectionId) {
      window.location.assign(url.href);
      return;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();
    this.setActiveRailItem(currentSection, url);
    currentContent.classList.add('instamart-collection__content--loading');
    currentContent.setAttribute('aria-busy', 'true');

    try {
      const sectionUrl = new URL(url.href);
      sectionUrl.searchParams.set('section_id', sectionId);
      const response = await fetch(sectionUrl, { signal: this.abortController.signal });
      if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);

      const html = await response.text();
      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const replacement = parsedDocument.querySelector('collection-component');
      const replacementContent = replacement?.querySelector('.instamart-collection__content');
      if (!replacement || !replacementContent) throw new Error('Collection content was missing from the response');

      window.instamartSortDrawer?.close(false);
      window.instamartCollectionCart?.closePicker(false);
      currentContent.replaceWith(replacementContent);
      currentComponent.dataset.collectionId = replacement.dataset.collectionId || '';
      currentSection.dataset.hasMatchingCollection = replacement.querySelector('.instamart-collection')?.dataset.hasMatchingCollection || 'true';
      window.instamartCollectionCart?.render();
      this.currentPath = url.pathname;
      if (updateHistory) {
        history.pushState({ instamartCollectionSwitch: true }, '', `${url.pathname}${url.search}`);
      }

      const title = replacement.querySelector('.instamart-collection__title')?.textContent?.trim();
      const currentTitle = currentSection.querySelector('.instamart-collection__title');
      if (title && currentTitle) currentTitle.textContent = title;
      const storeTitle = document.title.split(' - ').slice(1).join(' - ');
      if (title) document.title = storeTitle ? `${title} - ${storeTitle}` : title;
    } catch (error) {
      if (error.name === 'AbortError') return;
      currentContent.classList.remove('instamart-collection__content--loading');
      currentContent.removeAttribute('aria-busy');
      this.setActiveRailItem(currentSection, new URL(this.currentPath, window.location.origin));
      console.error('Unable to switch collection', error);
    }
  }

  setActiveRailItem(section, url) {
    section.querySelectorAll('.instamart-collection__rail-item').forEach((item) => {
      const isActive = new URL(item.href, window.location.origin).pathname === url.pathname;
      item.classList.toggle('instamart-collection__rail-item--active', isActive);
      if (isActive) item.setAttribute('aria-current', 'page');
      else item.removeAttribute('aria-current');
    });
  }

}

if (!window.instamartCollectionNavigation) {
  window.instamartCollectionNavigation = new InstamartCollectionNavigation();
}

class InstamartSortDrawer {
  constructor() {
    this.drawer = null;
    this.trigger = null;
    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    document.addEventListener('click', this.onClick);
    document.addEventListener('change', this.onChange);
    document.addEventListener('keydown', this.onKeydown);
  }

  onClick(event) {
    const openButton = event.target.closest('[data-sort-drawer-open]');
    if (openButton) {
      this.open(openButton);
      return;
    }

    if (event.target.closest('[data-sort-drawer-close]')) this.close();
  }

  onChange(event) {
    if (!event.target.matches('.instamart-sort-drawer input[name="sort_by"]')) return;

    window.setTimeout(() => this.close(), 120);
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.drawer?.classList.contains('is-open')) this.close();
  }

  open(trigger) {
    const drawer = trigger.closest('instamart-sort-drawer')?.querySelector('[data-sort-drawer]');
    if (!drawer) return;

    this.drawer = drawer;
    this.trigger = trigger;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('instamart-sort-open');

    const panel = drawer.querySelector('[data-sort-drawer-panel]');
    window.requestAnimationFrame(() => {
      if (typeof trapFocus === 'function') trapFocus(panel, drawer.querySelector('.instamart-sort-drawer__close'));
      else panel?.focus();
    });
  }

  close(restoreFocus = true) {
    if (!this.drawer) return;

    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.trigger?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('instamart-sort-open');

    if (typeof removeTrapFocus === 'function') removeTrapFocus(restoreFocus ? this.trigger : null);
    else if (restoreFocus) this.trigger?.focus();

    this.drawer = null;
    this.trigger = null;
  }
}

if (!window.instamartSortDrawer) {
  window.instamartSortDrawer = new InstamartSortDrawer();
}

class InstamartCollectionCart {
  constructor() {
    this.cart = { item_count: 0, items: [] };
    this.product = null;
    this.selectedVariantId = null;
    this.trigger = null;
    this.pendingQuantities = new Map();
    this.quantitySyncTimer = null;
    this.quantitySyncing = false;
    this.productCache = new WeakMap();
    this.prefetchedProducts = new WeakSet();
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onImageIntent = this.onImageIntent.bind(this);
    document.addEventListener('click', this.onClick);
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('pointerover', this.onImageIntent, { passive: true });
    document.addEventListener('pointerdown', this.onImageIntent, { passive: true });
    document.addEventListener('focusin', this.onImageIntent);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.source === 'instamart-collection') return;
        if (Array.isArray(event?.cartData?.items) && Number.isFinite(event.cartData.item_count)) {
          this.cart = event.cartData;
          this.applyPendingQuantities();
          this.render();
        } else {
          this.loadCart();
        }
      });
    }
    this.loadCart();
  }

  cartEndpoint(action = '') {
    const base = window.routes?.cart_url || '/cart';
    return action ? `${base}/${action}.js` : `${base}.js`;
  }

  async loadCart(shouldRender = true) {
    try {
      const response = await fetch(this.cartEndpoint(), { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Unable to load cart');
      this.cart = await response.json();
      if (shouldRender) this.render();
    } catch (error) {
      console.error(error);
    }
  }

  productFromControl(control) {
    if (this.productCache.has(control)) return this.productCache.get(control);
    const script = control.querySelector('[data-instamart-product-json]');
    if (!script) return null;
    try {
      const product = JSON.parse(script.textContent);
      this.productCache.set(control, product);
      return product;
    } catch (error) {
      console.error('Invalid collection product data', error);
      return null;
    }
  }

  quantityFor(variantId) {
    return this.cart.items
      .filter((item) => Number(item.variant_id) === Number(variantId))
      .reduce((total, item) => total + item.quantity, 0);
  }

  async onClick(event) {
    const close = event.target.closest('[data-variant-close]');
    if (close) {
      this.closePicker();
      return;
    }

    const carouselArrow = event.target.closest('[data-variant-carousel-arrow]');
    if (carouselArrow) {
      const variants = this.picker?.querySelector('.instamart-variant-picker__variants');
      if (!variants) return;
      variants.scrollBy({
        left: (carouselArrow.dataset.variantCarouselArrow === 'next' ? 1 : -1) * variants.clientWidth,
        behavior: 'smooth',
      });
      return;
    }

    const pickerAction = event.target.closest('[data-picker-variant-add], [data-picker-variant-action]');
    if (pickerAction) {
      pickerAction.blur();
      const variantId = Number(pickerAction.dataset.variantId);
      if (!variantId) return;
      this.selectedVariantId = variantId;
      this.trigger?.closest('[data-instamart-product]')?.setAttribute('data-active-variant', variantId);
      const current = this.quantityFor(variantId);
      const quantity = pickerAction.matches('[data-picker-variant-add]') || pickerAction.dataset.pickerVariantAction === 'increase'
        ? current + 1
        : Math.max(0, current - 1);
      this.changeQuantity(variantId, quantity);
      return;
    }

    const action = event.target.closest('[data-cart-action]');
    if (action) {
      action.blur();
      const control = action.closest('[data-instamart-product]');
      const variantId = Number(control.dataset.activeVariant || control.dataset.defaultVariant);
      const current = this.quantityFor(variantId);
      const quantity = action.dataset.cartAction === 'increase' ? current + 1 : Math.max(0, current - 1);
      this.changeQuantity(variantId, quantity);
      return;
    }

    const addButton = event.target.closest('.instamart-quick-add__button');
    if (!addButton) return;
    const control = addButton.closest('[data-instamart-product]');
    const product = this.productFromControl(control);
    if (!product) return;

    if (product.hasVariants) {
      this.openPicker(product, addButton);
      return;
    }

    const variantId = Number(control.dataset.defaultVariant);
    this.changeQuantity(variantId, this.quantityFor(variantId) + 1);
  }

  onImageIntent(event) {
    const button = event.target.closest('.instamart-quick-add__button');
    const control = button?.closest('[data-instamart-product]');
    if (!control || this.prefetchedProducts.has(control)) return;
    const product = this.productFromControl(control);
    if (!product?.hasVariants) return;

    this.prefetchedProducts.add(control);
    product.variants.slice(0, 3).forEach((variant) => {
      const imageUrl = variant.image || product.image;
      if (!imageUrl) return;
      const image = new Image();
      image.decoding = 'async';
      image.fetchPriority = event.type === 'pointerdown' ? 'high' : 'low';
      image.sizes = '(min-width: 600px) 220px, (max-width: 370px) calc((100vw - 3.7rem) / 2), calc((100vw - 6.4rem) / 3)';
      image.srcset = this.imageSrcset(imageUrl);
      image.src = imageUrl;
    });
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.picker && !this.picker.hidden) this.closePicker();
  }

  openPicker(product, trigger) {
    this.picker = document.querySelector('[data-instamart-variant-picker]');
    if (!this.picker) return;
    this.product = product;
    this.trigger = trigger;
    const control = trigger.closest('[data-instamart-product]');
    const preferredId = Number(control?.dataset.activeVariant);
    const firstAvailable = product.variants.find((variant) => variant.available && variant.id === preferredId)
      || product.variants.find((variant) => variant.available && this.quantityFor(variant.id) > 0)
      || product.variants.find((variant) => variant.available)
      || product.variants[0];
    this.selectedVariantId = firstAvailable?.id || null;
    control?.setAttribute('data-active-variant', this.selectedVariantId);
    this.picker.hidden = false;
    document.body.classList.add('instamart-picker-open');
    this.renderPicker();
    window.requestAnimationFrame(() => {
      const sheet = this.picker.querySelector('.instamart-variant-picker__sheet');
      const firstAction = sheet?.querySelector('[data-picker-variant-add], [data-picker-variant-action]');
      if (typeof trapFocus === 'function') trapFocus(sheet, firstAction || sheet);
      else sheet?.focus();
    });
  }

  closePicker(restoreFocus = true) {
    if (!this.picker) return;
    this.picker.hidden = true;
    document.body.classList.remove('instamart-picker-open');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(restoreFocus ? this.trigger : null);
    else if (restoreFocus) this.trigger?.focus();
    this.product = null;
    this.selectedVariantId = null;
  }

  renderPicker() {
    if (!this.picker || !this.product) return;
    const optionRoot = this.picker.querySelector('[data-variant-options]');
    const variantCards = this.product.variants.map((variant, index) => {
      const discount = variant.compareAtPrice > variant.price
        ? Math.round((variant.compareAtPrice - variant.price) * 100 / variant.compareAtPrice)
        : 0;
      const primaryLabel = variant.title === 'Default Title' ? this.product.title : (variant.options[0] || variant.title);
      const secondaryLabel = variant.options.slice(1).join(' · ');
      const imageUrl = variant.image || this.product.image;
      const imageSrcset = this.imageSrcset(imageUrl);
      const quantity = this.quantityFor(variant.id);
      return `<article class="instamart-variant-card${variant.available ? '' : ' is-unavailable'}" data-picker-card-variant="${variant.id}" style="--variant-order: ${index}">
        <div class="instamart-variant-card__media">
          ${imageUrl ? `<img src="${this.escape(imageUrl)}"${imageSrcset ? ` srcset="${this.escape(imageSrcset)}" sizes="(min-width: 600px) 220px, (max-width: 370px) calc((100vw - 3.7rem) / 2), calc((100vw - 6.4rem) / 3)"` : ''} alt="" width="320" height="320" loading="${index < 3 ? 'eager' : 'lazy'}" fetchpriority="${index < 2 ? 'high' : 'auto'}" decoding="async">` : '<span class="instamart-variant-card__placeholder" aria-hidden="true"></span>'}
          ${this.variantActionMarkup(variant)}
        </div>
        <div class="instamart-variant-card__details">
          <strong>${this.escape(primaryLabel)}</strong>
          ${secondaryLabel ? `<small>${this.escape(secondaryLabel)}</small>` : '<small>&nbsp;</small>'}
          ${discount ? `<span class="instamart-variant-card__discount">${discount}% <small>OFF</small></span>` : ''}
          <span class="instamart-variant-card__price"><b>${this.escape(variant.formattedPrice)}</b>${discount && variant.formattedCompareAtPrice ? `<s>${this.escape(variant.formattedCompareAtPrice)}</s>` : ''}</span>
        </div>
      </article>`;
    }).join('');
    optionRoot.innerHTML = `<div class="instamart-variant-picker__variants">${variantCards}</div>`;
    this.setText('[data-picker-product-title]', this.product.title);
  }

  variantActionMarkup(variant) {
    const quantity = this.quantityFor(variant.id);
    if (!variant.available) return '<span class="instamart-variant-card__sold-out">Sold out</span>';
    return `<button class="instamart-variant-card__add" type="button" data-picker-variant-add data-variant-id="${variant.id}" aria-label="Add variant to cart"${quantity > 0 ? ' hidden' : ''}>ADD</button>
      <div class="instamart-quantity instamart-variant-card__quantity"${quantity === 0 ? ' hidden' : ''}>
        <button type="button" data-picker-variant-action="decrease" data-variant-id="${variant.id}" aria-label="Decrease variant quantity">&minus;</button>
        <span class="instamart-quantity__value" aria-live="polite">${quantity}</span>
        <button type="button" data-picker-variant-action="increase" data-variant-id="${variant.id}" aria-label="Increase variant quantity">+</button>
      </div>`;
  }

  imageSrcset(imageUrl) {
    if (!imageUrl) return '';
    try {
      return [200, 400, 600].map((width) => {
        const url = new URL(imageUrl, window.location.origin);
        url.searchParams.set('width', width);
        return `${url.href} ${width}w`;
      }).join(', ');
    } catch (error) {
      return '';
    }
  }

  updatePickerActions() {
    if (!this.product || !this.picker || this.picker.hidden) return;
    this.product.variants.forEach((variant) => {
      const card = this.picker.querySelector(`[data-picker-card-variant="${variant.id}"]`);
      if (!card || !variant.available) return;
      const quantity = this.quantityFor(variant.id);
      const addButton = card.querySelector('.instamart-variant-card__add');
      const quantityControl = card.querySelector('.instamart-variant-card__quantity');
      addButton.hidden = quantity > 0;
      quantityControl.hidden = quantity === 0;
      quantityControl.querySelector('.instamart-quantity__value').textContent = quantity;
    });
  }

  changeQuantity(variantId, quantity) {
    this.clearError();
    this.setLocalQuantity(variantId, quantity);
    this.pendingQuantities.set(Number(variantId), quantity);
    this.renderQuantity(variantId);
    this.renderCartCount();
    window.updateCartCount?.(this.cart.item_count);

    window.clearTimeout(this.quantitySyncTimer);
    this.quantitySyncTimer = window.setTimeout(() => this.syncQuantities(), 80);
  }

  renderQuantity(variantId) {
    document.querySelectorAll('[data-instamart-product]').forEach((control) => {
      const activeId = Number(control.dataset.activeVariant || control.dataset.defaultVariant);
      if (activeId !== Number(variantId)) return;
      const product = this.productFromControl(control);
      if (product?.hasVariants) {
        control.querySelector('.instamart-quick-add__button').hidden = false;
        control.querySelector('.instamart-quantity').hidden = true;
        return;
      }
      const quantity = this.quantityFor(variantId);
      control.querySelector('.instamart-quick-add__button').hidden = quantity > 0;
      const quantityControl = control.querySelector('.instamart-quantity');
      quantityControl.hidden = quantity === 0;
      quantityControl.querySelector('.instamart-quantity__value').textContent = quantity;
    });

    if (!this.product || !this.picker || this.picker.hidden) return;
    const card = this.picker.querySelector(`[data-picker-card-variant="${variantId}"]`);
    if (!card) return;
    const quantity = this.quantityFor(variantId);
    card.querySelector('.instamart-variant-card__add').hidden = quantity > 0;
    const quantityControl = card.querySelector('.instamart-variant-card__quantity');
    quantityControl.hidden = quantity === 0;
    quantityControl.querySelector('.instamart-quantity__value').textContent = quantity;
  }

  setLocalQuantity(variantId, quantity) {
    const previousQuantity = this.quantityFor(variantId);
    const currentLine = this.cart.items.find((item) => Number(item.variant_id) === Number(variantId));
    const nextItems = currentLine
      ? this.cart.items
          .map((item) => Number(item.variant_id) === Number(variantId) ? { ...item, quantity } : item)
      : quantity > 0
        ? [
            ...this.cart.items,
            {
              variant_id: variantId,
              quantity,
              image: this.product?.variants.find((variant) => Number(variant.id) === Number(variantId))?.image || this.product?.image,
            },
          ]
        : this.cart.items;
    this.cart = {
      ...this.cart,
      item_count: Math.max(0, this.cart.item_count + quantity - previousQuantity),
      items: nextItems,
    };
  }

  applyPendingQuantities() {
    this.pendingQuantities.forEach((quantity, variantId) => this.setLocalQuantity(variantId, quantity));
  }

  async syncQuantities() {
    if (this.quantitySyncing || this.pendingQuantities.size === 0) return;
    this.quantitySyncing = true;
    let sections;
    const changedVariantIds = new Set();

    try {
      while (this.pendingQuantities.size > 0) {
        const [variantId, quantity] = this.pendingQuantities.entries().next().value;
        changedVariantIds.add(variantId);
        const currentLine = this.cart.items.find((item) =>
          Number(item.variant_id) === Number(variantId) && item.key
        );
        if (!currentLine && quantity === 0) {
          this.pendingQuantities.delete(variantId);
          this.cart = {
            ...this.cart,
            items: this.cart.items.filter((item) => Number(item.variant_id) !== Number(variantId)),
          };
          continue;
        }
        const url = currentLine ? this.cartEndpoint('change') : this.cartEndpoint('add');
        const body = currentLine
          ? {
              id: currentLine.key,
              quantity,
              sections: ['cart-drawer', 'cart-icon-bubble'],
              sections_url: window.location.pathname,
            }
          : {
              items: [{ id: variantId, quantity: Math.max(1, quantity) }],
              sections: ['cart-drawer', 'cart-icon-bubble'],
              sections_url: window.location.pathname,
            };
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Cart-Count-Optimistic': 'true',
          },
          body: JSON.stringify(body),
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || 'Cart could not be updated');

        if (this.pendingQuantities.get(variantId) === quantity) {
          this.pendingQuantities.delete(variantId);
        }
        sections = result.sections || sections;
        if (currentLine) {
          this.cart = result;
        } else {
          const addedItem = result.items?.[0] || result;
          this.cart = {
            ...this.cart,
            items: this.cart.items.map((item) =>
              Number(item.variant_id) === Number(variantId) ? addedItem : item
            ),
          };
        }
        this.applyPendingQuantities();
      }

      const response = await fetch(this.cartEndpoint(), {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) throw new Error('Cart could not be refreshed');
      this.cart = await response.json();
      this.applyPendingQuantities();
      this.renderCartSections(this.pendingQuantities.size === 0 ? sections : undefined);
      changedVariantIds.forEach((variantId) => this.renderQuantity(variantId));
      this.pendingQuantities.forEach((quantity, variantId) => this.renderQuantity(variantId));

      if (
        this.pendingQuantities.size === 0 &&
        typeof publish === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'instamart-collection',
          cartData: this.cart,
        });
      }
    } catch (error) {
      this.pendingQuantities.clear();
      await this.loadCart(false);
      this.showError(error.message);
      this.renderCartSections();
      this.render(false);
    } finally {
      this.quantitySyncing = false;
      if (this.pendingQuantities.size > 0) {
        this.quantitySyncTimer = window.setTimeout(() => this.syncQuantities(), 0);
      }
    }
  }

  renderCartSections(sections) {
    this.renderCartCount();

    if (!sections) return;

    const drawer = document.querySelector('cart-drawer');
    if (drawer && sections['cart-drawer']) {
      const source = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html').querySelector('#CartDrawer');
      const target = drawer.querySelector('#CartDrawer');
      if (source && target) {
        target.innerHTML = source.innerHTML;
        drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
      }
      drawer.classList.toggle('is-empty', this.cart.item_count === 0);
    }

    if (sections['cart-icon-bubble']) {
      const source = new DOMParser()
        .parseFromString(sections['cart-icon-bubble'], 'text/html')
        .querySelector('#cart-icon-bubble');
      const target = document.querySelector('#cart-icon-bubble');
      if (source && target) target.innerHTML = source.innerHTML;
    }

    this.renderCartCount();
  }

  renderCartCount() {
    const floatingCount = document.querySelector('[data-floating-cart-count]');
    const floatingDescription = document.querySelector('[data-floating-cart-description]');
    if (floatingCount) {
      floatingCount.textContent = this.cart.item_count > 99 ? '99+' : this.cart.item_count;
      floatingCount.classList.toggle('is-empty', this.cart.item_count === 0);
    }
    if (floatingDescription) {
      floatingDescription.textContent = `${this.cart.item_count} item${this.cart.item_count === 1 ? '' : 's'} in cart`;
    }

    document.querySelectorAll('[id="cart-icon-bubble"]').forEach((cartLink) => {
      let bubble = cartLink.querySelector('.cart-count-bubble');
      if (this.cart.item_count === 0) {
        bubble?.remove();
        return;
      }

      if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        bubble.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
        cartLink.appendChild(bubble);
      }

      const visibleCount = bubble.querySelector('[aria-hidden="true"]');
      const accessibleCount = bubble.querySelector('.visually-hidden');
      if (visibleCount) visibleCount.textContent = this.cart.item_count > 99 ? '99+' : this.cart.item_count;
      if (accessibleCount) {
        accessibleCount.textContent = `${this.cart.item_count} item${this.cart.item_count === 1 ? '' : 's'} in cart`;
      }
    });
  }

  render(updatePicker = false) {
    document.querySelectorAll('[data-instamart-product]').forEach((control) => {
      const product = this.productFromControl(control);
      if (!product) return;
      if (!control.dataset.activeVariant && product.hasVariants) {
        const cartedVariant = product.variants.find((variant) => this.quantityFor(variant.id) > 0);
        if (cartedVariant) control.dataset.activeVariant = cartedVariant.id;
      }
      const activeId = Number(control.dataset.activeVariant || control.dataset.defaultVariant);
      if (product.hasVariants) {
        control.querySelector('.instamart-quick-add__button').hidden = false;
        control.querySelector('.instamart-quantity').hidden = true;
        return;
      }
      const quantity = this.quantityFor(activeId);
      control.querySelector('.instamart-quick-add__button').hidden = quantity > 0;
      const quantityControl = control.querySelector('.instamart-quantity');
      quantityControl.hidden = quantity === 0;
      quantityControl.querySelector('.instamart-quantity__value').textContent = quantity;
    });

    document.querySelectorAll('[data-instamart-cart-bar]').forEach((bar) => {
      bar.hidden = this.cart.item_count === 0;
      const label = `${this.cart.item_count} ${this.cart.item_count === 1 ? 'ITEM' : 'ITEMS'}`;
      bar.querySelector('[data-cart-items]').textContent = label;
    });
    if (this.product && this.picker && !this.picker.hidden) {
      if (updatePicker) this.renderPicker();
      else this.updatePickerActions();
    }
  }

  setText(selector, value) {
    const element = this.picker.querySelector(selector);
    if (element) element.textContent = value;
  }

  showError(message) {
    const error = this.picker?.querySelector('[data-picker-error]');
    if (error) {
      error.textContent = message;
      error.hidden = false;
    }
  }

  clearError() {
    const error = this.picker?.querySelector('[data-picker-error]');
    if (error) error.hidden = true;
  }

  escape(value) {
    const element = document.createElement('span');
    element.textContent = String(value ?? '');
    return element.innerHTML;
  }
}

if (!window.instamartCollectionCart) {
  window.instamartCollectionCart = new InstamartCollectionCart();
}
